import { useEffect, useState } from "react";
import type { Lang } from "../lib/i18n";
import { coachMarks } from "../data/coachMarks";

/** Floating per-step "step guide" (fD) — a guide FAB that opens a coach-mark popup. */
export function CoachMark({ step, lang }: { step: number; lang: Lang }) {
  const [open, setOpen] = useState(true);
  const [pulse, setPulse] = useState(false);

  // Re-open the guide whenever the step changes.
  useEffect(() => {
    setOpen(true);
  }, [step]);

  if (step < 1) return null;
  const guide = coachMarks[step]?.[lang];
  if (!guide) return null;

  return (
    <>
      <button
        className={`kd-guide-fab${pulse && !open ? " is-pulse" : ""}${open ? " is-open" : ""}`}
        onClick={() => {
          setOpen((o) => !o);
          setPulse(false);
        }}
        aria-label={lang === "ar" ? "فتح دليل الخطوة" : "Open step guide"}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <circle cx="9" cy="10" r="0.6" fill="currentColor" />
            <circle cx="13" cy="10" r="0.6" fill="currentColor" />
            <circle cx="17" cy="10" r="0.6" fill="currentColor" />
          </svg>
        )}
        {!open && <span className="kd-guide-fab-label">{lang === "ar" ? "دليل الخطوة" : "Step guide"}</span>}
      </button>

      {open && (
        <div className="kd-guide-popup" dir={lang === "ar" ? "rtl" : "ltr"} role="dialog">
          <div className="kd-guide-popup-header">
            <div className="kd-guide-popup-kicker">{guide.kicker}</div>
            <button className="kd-guide-popup-close" onClick={() => setOpen(false)} aria-label={lang === "ar" ? "إغلاق" : "Close"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="kd-guide-popup-body">
            <h3 className="kd-guide-popup-title">{guide.title}</h3>
            <p className="kd-guide-popup-desc">{guide.desc}</p>
            <ul className="kd-guide-popup-list">
              {guide.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
            {guide.chips?.length > 0 && (
              <div className="kd-guide-popup-chips">
                {guide.chips.map((c, i) => (
                  <span className="kd-guide-chip" key={i}>{c}</span>
                ))}
              </div>
            )}
            {guide.actions?.length > 0 && (
              <div className="kd-guide-popup-actions">
                {guide.actions.map((a, i) =>
                  a.action === "print" ? (
                    <button className="kd-guide-action" onClick={() => window.print()} key={i}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9" />
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                        <rect x="6" y="14" width="12" height="8" />
                      </svg>
                      <span>{a.label}</span>
                    </button>
                  ) : (
                    <a className="kd-guide-action" href={a.href} {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} key={i}>
                      <span>{a.label}</span>
                    </a>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
